import { ensureDemoState, saveDemoState } from './store.js';
import { buildDocxBlob, buildXlsxBlob } from './exports.js';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const nowIso = () => new Date().toISOString();

const createId = (prefix) => {
  try {
    if (globalThis.crypto?.randomUUID) {
      return `${prefix}${globalThis.crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(16).slice(2)}`;
};

const normalizeValue = (value) => String(value ?? '').trim();
const toKey = (value) => normalizeValue(value).toLowerCase();
const UI_VERSION_VALUES = new Set([1, 2, 3]);

const normalizeUiVersion = (value) => {
  if (typeof value === 'number' && Number.isInteger(value) && UI_VERSION_VALUES.has(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) {
      const parsed = Number(trimmed);
      if (Number.isInteger(parsed) && UI_VERSION_VALUES.has(parsed)) {
        return parsed;
      }
    }
  }
  return 1;
};

const normalizeUser = (user) => {
  if (!user) return user;
  return {
    ...user,
    uiVersion: normalizeUiVersion(user.uiVersion),
  };
};

const parseUiVersionPayload = (value, fallback = 1) => {
  if (value === undefined || value === null || (typeof value === 'string' && !value.trim())) {
    return { ok: true, value: fallback };
  }
  let parsed = null;
  if (typeof value === 'number' && Number.isInteger(value)) {
    parsed = value;
  } else if (typeof value === 'string') {
    const candidate = Number(value.trim());
    if (Number.isInteger(candidate)) {
      parsed = candidate;
    }
  }
  if (!UI_VERSION_VALUES.has(parsed)) {
    return { ok: false, value: fallback };
  }
  return { ok: true, value: parsed };
};

const createBlobUrl = (blob, ttlMs = 60_000) => {
  const url = URL.createObjectURL(blob);
  if (ttlMs > 0) {
    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }, ttlMs);
  }
  return url;
};

const findTemplateById = (state, templateId) => {
  const system = state.exportTemplates?.systemTemplates ?? [];
  const user = state.exportTemplates?.userTemplates ?? [];
  return [...system, ...user].find((t) => t?.templateId === templateId) ?? null;
};

const sortByUpdated = (items) =>
  [...items].sort((a, b) => {
    const aTime = a?.updatedAt ? Date.parse(a.updatedAt) : a?.createdAt ? Date.parse(a.createdAt) : 0;
    const bTime = b?.updatedAt ? Date.parse(b.updatedAt) : b?.createdAt ? Date.parse(b.createdAt) : 0;
    return bTime - aTime;
  });

const cloneWithConversationMeta = (items, conversation) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    ...JSON.parse(JSON.stringify(item)),
    conversationId: conversation.conversationId,
    customerId: conversation.customerId ?? conversation.customer ?? null,
    projectId: conversation.projectId ?? conversation.project ?? null,
    meetingDate: conversation.meetingDate ?? null,
  }));

const getSeedResultsTemplate = (state, projectId) => {
  const normalizedProject = normalizeValue(projectId);
  if (!normalizedProject) return null;
  const conversationForProject = state.conversations.find((c) => normalizeValue(c.projectId) === normalizedProject);
  if (!conversationForProject) return null;
  return state.resultsByConversationId?.[conversationForProject.conversationId] ?? null;
};

const pickBaseResults = (state, { customerId, projectId, meetingDate, mode } = {}) => {
  const fromProject = getSeedResultsTemplate(state, projectId);
  const fallbackKey = state.conversations?.[0]?.conversationId;
  const fallback = fallbackKey ? state.resultsByConversationId?.[fallbackKey] : null;
  const template = fromProject ?? fallback;
  if (!template) {
    return { requirements: [], userStories: [] };
  }
  const requirements = cloneWithConversationMeta(template.requirements, {
    conversationId: '',
    customerId,
    projectId,
    meetingDate,
  });
  const userStories = cloneWithConversationMeta(template.userStories, {
    conversationId: '',
    customerId,
    projectId,
    meetingDate,
  });
  requirements.forEach((req) => {
    req.status = req.status ?? 'NEW';
  });
  return { requirements, userStories, mode: mode ?? template.conversation?.mode ?? 'upload' };
};

const updateUserJobStats = (state, userEmail) => {
  const user = state.users.find((u) => toKey(u.email) === toKey(userEmail));
  if (!user) return;
  const total = Number(user.jobStats?.total ?? 0) + 1;
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const monthly = Array.isArray(user.jobStats?.monthly) ? [...user.jobStats.monthly] : [];
  const existing = monthly.find((entry) => entry?.month === monthKey);
  if (existing) {
    existing.count = Number(existing.count ?? 0) + 1;
  } else {
    monthly.push({ month: monthKey, count: 1 });
  }
  const currentMonth = (monthly.find((entry) => entry?.month === monthKey)?.count) ?? 0;
  user.jobStats = { total, currentMonth, monthly };
};

export const currentUserRequest = async () => {
  const state = ensureDemoState();
  const currentEmail = state.currentUserEmail ?? state.users?.[0]?.email ?? '';
  const user = state.users.find((u) => toKey(u.email) === toKey(currentEmail)) ?? state.users?.[0] ?? null;
  return { user: normalizeUser(user) };
};

export const loginRequest = async (credentials = {}) => {
  const state = ensureDemoState();
  const email = normalizeValue(credentials.email || 'demo@anforderungsai.de').toLowerCase();
  let user = state.users.find((u) => toKey(u.email) === toKey(email)) ?? null;
  if (!user) {
    user = {
      email,
      uiVersion: 1,
      role: 'user',
      status: 'active',
      planId: 'package_m',
      billingStatus: 'active',
      profile: { firstName: '', lastName: '', company: '', phone: '', vatNumber: '', address: { street: '', postalCode: '', city: '', country: '' } },
      jobStats: { total: 0, currentMonth: 0, monthly: [] },
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    state.users.push(user);
  } else {
    user.uiVersion = normalizeUiVersion(user.uiVersion);
  }
  state.currentUserEmail = user.email;
  saveDemoState(state);
  return { token: 'demo', user: normalizeUser(user) };
};

export const registerRequest = async (payload = {}) => {
  const state = ensureDemoState();
  const email = normalizeValue(payload.email).toLowerCase();
  if (!email) {
    throw new Error('E-Mail fehlt.');
  }
  const exists = state.users.some((u) => toKey(u.email) === toKey(email));
  if (exists) {
    const error = new Error('E-Mail ist bereits registriert.');
    error.code = 'CONFLICT';
    throw error;
  }
  const user = {
    email,
    uiVersion: normalizeUiVersion(payload.uiVersion),
    role: 'user',
    status: 'active',
    planId: payload.planId ?? 'package_m',
    billingStatus: 'active',
    profile: {
      firstName: payload.firstName ?? '',
      lastName: payload.lastName ?? '',
      company: payload.company ?? '',
      phone: payload.phone ?? '',
      vatNumber: payload.vatNumber ?? '',
      address: {
        street: payload.street ?? '',
        postalCode: payload.postalCode ?? '',
        city: payload.city ?? '',
        country: payload.country ?? '',
      },
    },
    jobStats: { total: 0, currentMonth: 0, monthly: [] },
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  state.users.push(user);
  state.currentUserEmail = user.email;
  saveDemoState(state);
  return { token: 'demo', user: normalizeUser(user) };
};

export const logoutRequest = async () => null;

export const requestPasswordReset = async () => ({ ok: true });

export const resetPasswordRequest = async () => ({ ok: true });

export const listUsersRequest = async () => {
  const state = ensureDemoState();
  return { users: (state.users ?? []).map((user) => normalizeUser(user)) };
};

export const createUserRequest = async (payload = {}) => {
  const state = ensureDemoState();
  const email = normalizeValue(payload.email).toLowerCase();
  if (!email) throw new Error('E-Mail fehlt.');
  if (state.users.some((u) => toKey(u.email) === toKey(email))) {
    const error = new Error('E-Mail existiert bereits.');
    error.code = 'CONFLICT';
    throw error;
  }
  const parsedUiVersion = parseUiVersionPayload(payload.uiVersion, 1);
  if (!parsedUiVersion.ok) {
    const error = new Error('Ungültige UI-Version.');
    error.code = 'BAD_REQUEST';
    throw error;
  }
  const user = {
    email,
    uiVersion: parsedUiVersion.value,
    role: payload.role ?? 'user',
    status: 'active',
    planId: payload.planId ?? 'package_m',
    billingStatus: 'active',
    profile: payload.profile ?? payload,
    jobStats: { total: 0, currentMonth: 0, monthly: [] },
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  if (user.profile && !user.profile.address) {
    user.profile.address = {
      street: payload.street ?? '',
      postalCode: payload.postalCode ?? '',
      city: payload.city ?? '',
      country: payload.country ?? '',
    };
  }
  state.users.push(user);
  saveDemoState(state);
  return { user: normalizeUser(user) };
};

export const updateUserRequest = async (email, payload = {}) => {
  const state = ensureDemoState();
  const key = toKey(email);
  const user = state.users.find((u) => toKey(u.email) === key);
  if (!user) {
    const error = new Error('Benutzer nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }

  if (payload.planId) user.planId = payload.planId;
  if (payload.role) user.role = payload.role;
  if (payload.status) user.status = payload.status;
  if (payload.uiVersion !== undefined) {
    const parsedUiVersion = parseUiVersionPayload(payload.uiVersion, normalizeUiVersion(user.uiVersion));
    if (!parsedUiVersion.ok) {
      const error = new Error('Ungültige UI-Version.');
      error.code = 'BAD_REQUEST';
      throw error;
    }
    user.uiVersion = parsedUiVersion.value;
  }
  if (payload.clearBilling) {
    user.billingStatus = null;
    user.billing = null;
  }

  if (payload.profile && typeof payload.profile === 'object') {
    const incoming = payload.profile;
    user.profile = user.profile ?? { address: {} };
    user.profile.firstName = incoming.firstName ?? user.profile.firstName ?? '';
    user.profile.lastName = incoming.lastName ?? user.profile.lastName ?? '';
    user.profile.company = incoming.company ?? user.profile.company ?? '';
    if (incoming.phone !== undefined) user.profile.phone = incoming.phone ?? '';
    if (incoming.vatNumber !== undefined) user.profile.vatNumber = incoming.vatNumber ?? '';
    const address = user.profile.address ?? {};
    const nextAddress = {
      ...address,
      street: incoming.street ?? address.street ?? '',
      postalCode: incoming.postalCode ?? address.postalCode ?? '',
      city: incoming.city ?? address.city ?? '',
      country: incoming.country ?? address.country ?? '',
    };
    user.profile.address = nextAddress;
  }

  user.updatedAt = nowIso();
  saveDemoState(state);
  return { user: normalizeUser(user) };
};

export const deleteUserRequest = async (email) => {
  const state = ensureDemoState();
  const key = toKey(email);
  const idx = state.users.findIndex((u) => toKey(u.email) === key);
  if (idx === -1) {
    const error = new Error('Benutzer nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  const [removed] = state.users.splice(idx, 1);
  if (toKey(state.currentUserEmail) === key) {
    state.currentUserEmail = state.users?.[0]?.email ?? '';
  }
  saveDemoState(state);
  return { user: normalizeUser(removed) };
};

export const listConversations = async ({ limit, all } = {}) => {
  const state = ensureDemoState();
  const ordered = sortByUpdated(state.conversations ?? []);
  if (all) return ordered;
  const safeLimit = Number(limit ?? 0);
  if (Number.isFinite(safeLimit) && safeLimit > 0) {
    return ordered.slice(0, safeLimit);
  }
  return ordered;
};

export const getConversation = async (conversationId) => {
  const state = ensureDemoState();
  const conversation = (state.conversations ?? []).find((c) => c.conversationId === conversationId) ?? null;
  if (!conversation) {
    const error = new Error('Job nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  return { conversation };
};

export const getResults = async (conversationId) => {
  const state = ensureDemoState();
  const data = state.resultsByConversationId?.[conversationId] ?? null;
  if (!data) {
    const error = new Error('Ergebnis nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  return {
    conversation: data.conversation ?? state.conversations.find((c) => c.conversationId === conversationId) ?? null,
    requirements: Array.isArray(data.requirements) ? data.requirements : [],
    userStories: Array.isArray(data.userStories) ? data.userStories : [],
  };
};

export const deleteConversation = async (conversationId) => {
  const state = ensureDemoState();
  state.conversations = (state.conversations ?? []).filter((c) => c.conversationId !== conversationId);
  if (state.resultsByConversationId) {
    delete state.resultsByConversationId[conversationId];
  }
  saveDemoState(state);
  return { ok: true };
};

export const updateConversation = async (conversationId, updates = {}) => {
  const state = ensureDemoState();
  const conversation = (state.conversations ?? []).find((c) => c.conversationId === conversationId);
  if (!conversation) {
    const error = new Error('Job nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }

  const nextCustomerId = updates.customerId ?? conversation.customerId ?? null;
  const nextProjectId = updates.projectId ?? conversation.projectId ?? null;
  const nextMeetingDate = updates.meetingDate ?? conversation.meetingDate ?? null;

  conversation.customerId = nextCustomerId;
  conversation.projectId = nextProjectId;
  conversation.meetingDate = nextMeetingDate;
  conversation.updatedAt = nowIso();

  const payload = state.resultsByConversationId?.[conversationId];
  if (payload) {
    const applyMeta = (item) => ({
      ...item,
      customerId: conversation.customerId,
      projectId: conversation.projectId,
      meetingDate: conversation.meetingDate,
    });
    payload.requirements = (payload.requirements ?? []).map(applyMeta);
    payload.userStories = (payload.userStories ?? []).map(applyMeta);
    payload.conversation = conversation;
  }

  saveDemoState(state);
  return { conversation };
};

const resolveItemId = (item) =>
  normalizeValue(item?.sortKey ?? item?.id ?? item?.itemId ?? item?.requirementId ?? '');

const recordChange = (conversation, { kind, id, field, from, to }) => {
  const logEntry = {
    at: nowIso(),
    kind,
    id,
    field,
    from: from ?? '',
    to: to ?? '',
  };
  conversation.changeLog = Array.isArray(conversation.changeLog) ? conversation.changeLog : [];
  conversation.changeLog.unshift(logEntry);
};

export const updateConversationItem = async (conversationId, payload = {}) => {
  const state = ensureDemoState();
  const data = state.resultsByConversationId?.[conversationId];
  const conversation = (state.conversations ?? []).find((c) => c.conversationId === conversationId);
  if (!data || !conversation) {
    const error = new Error('Job nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }

  const kind = payload.kind === 'userStory' ? 'userStory' : 'requirement';
  const updates = payload.updates && typeof payload.updates === 'object' ? payload.updates : {};
  const key = normalizeValue(payload.sortKey ?? payload.itemId ?? '');
  if (!key) {
    throw new Error('sortKey fehlt.');
  }

  const list = kind === 'userStory' ? (data.userStories ?? []) : (data.requirements ?? []);
  const index = list.findIndex((item) => resolveItemId(item) === key);
  if (index === -1) {
    const error = new Error('Item nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }

  const item = list[index];
  const id = resolveItemId(item) || key;

  const applyField = (field, next) => {
    const previous = item[field];
    if (previous === next) return;
    item[field] = next;
    recordChange(conversation, { kind, id, field, from: previous, to: next });
  };

  if (kind === 'requirement') {
    if ('status' in updates) applyField('status', updates.status);
    if ('text' in updates) applyField('text', updates.text);
    if ('priority' in updates) applyField('priority', updates.priority);
    if ('category' in updates) applyField('type', updates.category);
    if ('topics' in updates) {
      const previous = Array.isArray(item.categories) ? item.categories.join(', ') : item.categories ?? '';
      const nextCategories = Array.isArray(updates.topics) ? updates.topics : String(updates.topics ?? '').split(',').map((v) => v.trim()).filter(Boolean);
      item.categories = nextCategories;
      recordChange(conversation, { kind, id, field: 'topics', from: previous, to: nextCategories.join(', ') });
    }
  } else {
    if ('story' in updates) applyField('story', updates.story);
    if ('role' in updates) applyField('role', updates.role);
    if ('goal' in updates) applyField('goal', updates.goal);
    if ('benefit' in updates) applyField('benefit', updates.benefit);
    if ('acceptance' in updates) applyField('acceptance', updates.acceptance);
    if ('priority' in updates) applyField('priority', updates.priority);
    if ('category' in updates) {
      const previous = Array.isArray(item.categories) ? item.categories.join(', ') : item.categories ?? '';
      const next = normalizeValue(updates.category);
      item.categories = next ? [next] : [];
      recordChange(conversation, { kind, id, field: 'category', from: previous, to: next });
    }
    if ('topics' in updates) {
      const previous = Array.isArray(item.categories) ? item.categories.join(', ') : item.categories ?? '';
      const nextCategories = Array.isArray(updates.topics) ? updates.topics : String(updates.topics ?? '').split(',').map((v) => v.trim()).filter(Boolean);
      item.categories = nextCategories;
      recordChange(conversation, { kind, id, field: 'topics', from: previous, to: nextCategories.join(', ') });
    }
  }

  conversation.updatedAt = nowIso();
  data.conversation = conversation;
  saveDemoState(state);

  return { item, changeLog: conversation.changeLog ?? [] };
};

export const listRequirements = async (filters = {}) => {
  const state = ensureDemoState();
  const allRequirements = Object.values(state.resultsByConversationId ?? {})
    .flatMap((entry) => (Array.isArray(entry?.requirements) ? entry.requirements : []))
    .map((req) => req);

  const search = toKey(filters.search);
  const customerId = normalizeValue(filters.customerId);
  const projectId = normalizeValue(filters.projectId);
  const status = normalizeValue(filters.status).toUpperCase();
  const priority = toKey(filters.priority);
  const type = normalizeValue(filters.type);
  const from = normalizeValue(filters.from);
  const to = normalizeValue(filters.to);

  const matchesDate = (meetingDate) => {
    const date = normalizeValue(meetingDate);
    if (!date) return true;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  };

  let items = allRequirements.filter((req) => {
    if (customerId && normalizeValue(req.customerId ?? req.customer) !== customerId) return false;
    if (projectId && normalizeValue(req.projectId ?? req.project) !== projectId) return false;
    if (status && String(req.status ?? 'NEW').toUpperCase() !== status) return false;
    if (priority && toKey(req.priority) !== priority) return false;
    if (type && normalizeValue(req.type) !== type) return false;
    if (!matchesDate(req.meetingDate)) return false;
    if (!search) return true;
    const haystack = [
      req.text,
      req.requirement,
      req.type,
      req.priority,
      req.status,
      req.customerId,
      req.projectId,
      req.meetingDate,
      req.conversationId,
      resolveItemId(req),
    ]
      .filter(Boolean)
      .map((v) => String(v))
      .join(' ')
      .toLowerCase();
    return haystack.includes(search);
  });

  const safeLimit = Number(filters.limit ?? 0);
  if (Number.isFinite(safeLimit) && safeLimit > 0) {
    items = items.slice(0, safeLimit);
  }

  return { requirements: items };
};

const parseTimestamp = (value) => {
  const parsed = Date.parse(normalizeValue(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const compareByLatest = (left, right) => parseTimestamp(right?.latestUpdatedAt) - parseTimestamp(left?.latestUpdatedAt);

export const listCustomerProjects = async () => {
  const state = ensureDemoState();
  const conversations = Array.isArray(state.conversations) ? state.conversations : [];
  const customersMap = new Map();

  conversations.forEach((conversation) => {
    const customerId = normalizeValue(conversation?.customerId ?? conversation?.customer);
    const projectId = normalizeValue(conversation?.projectId ?? conversation?.project);
    if (!customerId || !projectId) return;

    const conversationTime = normalizeValue(conversation?.updatedAt ?? conversation?.createdAt);
    const customerKey = toKey(customerId);
    const projectKey = toKey(projectId);

    const customerEntry = customersMap.get(customerKey) ?? {
      customerId,
      conversationCount: 0,
      latestUpdatedAt: conversationTime,
      projects: new Map(),
    };

    customerEntry.conversationCount += 1;
    if (parseTimestamp(conversationTime) > parseTimestamp(customerEntry.latestUpdatedAt)) {
      customerEntry.latestUpdatedAt = conversationTime;
    }

    const projectEntry = customerEntry.projects.get(projectKey) ?? {
      projectId,
      conversationCount: 0,
      latestUpdatedAt: conversationTime,
    };

    projectEntry.conversationCount += 1;
    if (parseTimestamp(conversationTime) > parseTimestamp(projectEntry.latestUpdatedAt)) {
      projectEntry.latestUpdatedAt = conversationTime;
    }

    customerEntry.projects.set(projectKey, projectEntry);
    customersMap.set(customerKey, customerEntry);
  });

  const customers = Array.from(customersMap.values())
    .map((customerEntry) => {
      const projects = Array.from(customerEntry.projects.values())
        .sort(compareByLatest)
        .map((project) => ({
          projectId: project.projectId,
          conversationCount: project.conversationCount,
          latestUpdatedAt: project.latestUpdatedAt || null,
        }));

      return {
        customerId: customerEntry.customerId,
        projectCount: projects.length,
        conversationCount: customerEntry.conversationCount,
        latestUpdatedAt: customerEntry.latestUpdatedAt || null,
        projects,
      };
    })
    .sort(compareByLatest);

  return { customers };
};

const DEMO_SUMMARY_FALLBACK = {
  de: {
    whatItDoes: 'Für dieses Projekt liegen noch nicht genügend verwertbare Inhalte vor.',
    whatItIsAbout: 'Bitte lade weitere Gespräche oder Transkripte hoch.',
    nextSteps: ['Weitere Quellen hinzufügen und erneut zusammenfassen.'],
    highlights: ['Automatisch erzeugte Fallback-Zusammenfassung.'],
  },
  en: {
    whatItDoes: 'There is not enough usable content for this project yet.',
    whatItIsAbout: 'Please upload more conversations or transcripts.',
    nextSteps: ['Add more sources and retry the summary.'],
    highlights: ['Automatically generated fallback summary.'],
  },
  fr: {
    whatItDoes: 'Il n’y a pas encore assez de contenu exploitable pour ce projet.',
    whatItIsAbout: 'Ajoute plus de conversations ou de transcriptions.',
    nextSteps: ['Ajouter plus de sources puis relancer le résumé.'],
    highlights: ['Résumé de secours généré automatiquement.'],
  },
  es: {
    whatItDoes: 'Todavía no hay suficiente contenido utilizable para este proyecto.',
    whatItIsAbout: 'Sube más conversaciones o transcripciones.',
    nextSteps: ['Añade más fuentes y vuelve a generar el resumen.'],
    highlights: ['Resumen de respaldo generado automáticamente.'],
  },
};

const resolveSummaryLanguage = (language) => {
  const normalized = normalizeValue(language).toLowerCase();
  return normalized === 'en' || normalized === 'fr' || normalized === 'es' ? normalized : 'de';
};

const normalizeSummaryList = (items, limit = 3) =>
  (Array.isArray(items) ? items : [])
    .map((item) => normalizeValue(item))
    .filter(Boolean)
    .slice(0, limit);

const sortByPriority = (items = []) => {
  const priorityWeight = (value) => {
    const normalized = normalizeValue(value).toLowerCase();
    if (normalized.startsWith('must')) return 0;
    if (normalized.startsWith('should')) return 1;
    if (normalized.startsWith('could')) return 2;
    return 3;
  };

  return [...items].sort((left, right) => {
    const byPriority = priorityWeight(left?.priority) - priorityWeight(right?.priority);
    if (byPriority !== 0) return byPriority;
    return parseTimestamp(right?.updatedAt) - parseTimestamp(left?.updatedAt);
  });
};

export const getCustomerProjectSummary = async ({ customerId, projectId, language } = {}) => {
  const state = ensureDemoState();
  const normalizedCustomer = normalizeValue(customerId);
  const normalizedProject = normalizeValue(projectId);

  if (!normalizedCustomer || !normalizedProject) {
    const error = new Error('customerId und projectId sind erforderlich.');
    error.code = 'VALIDATION';
    throw error;
  }

  const customerKey = toKey(normalizedCustomer);
  const projectKey = toKey(normalizedProject);
  const conversations = Array.isArray(state.conversations) ? state.conversations : [];
  const scopedConversations = conversations.filter((conversation) => {
    const conversationCustomer = toKey(conversation?.customerId ?? conversation?.customer);
    const conversationProject = toKey(conversation?.projectId ?? conversation?.project);
    return conversationCustomer === customerKey && conversationProject === projectKey;
  });

  if (!scopedConversations.length) {
    const error = new Error('Keine Conversations fuer dieses Kunde/Projekt gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }

  const resultsByConversationId = state.resultsByConversationId ?? {};
  const scopedRequirements = scopedConversations
    .flatMap((conversation) => {
      const payload = resultsByConversationId[conversation.conversationId];
      const requirements = Array.isArray(payload?.requirements) ? payload.requirements : [];
      return requirements.map((requirement) => ({
        ...requirement,
        text: normalizeValue(requirement?.text || requirement?.requirement),
        status: normalizeValue(requirement?.status || 'NEW').toUpperCase(),
        updatedAt: normalizeValue(conversation?.updatedAt ?? conversation?.createdAt),
        conversationId: conversation.conversationId,
      }));
    })
    .filter((entry) => entry.text);

  const latestUpdatedAt = scopedConversations
    .map((conversation) => normalizeValue(conversation?.updatedAt ?? conversation?.createdAt))
    .sort((left, right) => parseTimestamp(right) - parseTimestamp(left))[0] || null;

  const summarizedConversations = new Set(scopedRequirements.map((entry) => entry.conversationId)).size;
  const sourceStats = {
    totalConversations: scopedConversations.length,
    summarizedConversations,
    latestUpdatedAt,
  };

  if (!scopedRequirements.length) {
    const fallback = DEMO_SUMMARY_FALLBACK[resolveSummaryLanguage(language)] ?? DEMO_SUMMARY_FALLBACK.de;
    return {
      customerId: normalizedCustomer,
      projectId: normalizedProject,
      ...fallback,
      sourceStats,
      usedFallback: true,
    };
  }

  const sortedByPriority = sortByPriority(scopedRequirements);
  const whatItDoes = sortedByPriority[0]?.text ?? scopedRequirements[0]?.text ?? '';
  const whatItIsAbout = sortedByPriority[1]?.text ?? sortedByPriority[0]?.text ?? '';
  const nextSteps = normalizeSummaryList(
    sortedByPriority
      .filter((entry) => entry.status !== 'DONE' && entry.status !== 'ARCHIVED')
      .map((entry) => entry.text),
    3,
  );
  const highlights = normalizeSummaryList(sortedByPriority.map((entry) => entry.text), 3);

  return {
    customerId: normalizedCustomer,
    projectId: normalizedProject,
    whatItDoes,
    whatItIsAbout,
    nextSteps,
    highlights,
    sourceStats,
    usedFallback: false,
  };
};

const normalizeQaContext = (context = {}) => {
  const scope = context?.scope === 'conversation' ? 'conversation' : 'global';
  const normalized = { scope };
  const assign = (field) => {
    const value = normalizeValue(context?.[field]);
    if (value && value !== '__none__') {
      normalized[field] = value;
    }
  };
  if (scope === 'conversation') {
    assign('conversationId');
  }
  assign('customerId');
  assign('projectId');
  assign('from');
  assign('to');
  return normalized;
};

const qaContextSignature = (context) => JSON.stringify(normalizeQaContext(context));

const ensureQaThreads = (state) => {
  if (!Array.isArray(state.qaThreads)) {
    state.qaThreads = [];
  }
  return state.qaThreads;
};

const resolveQaOwner = (state) =>
  normalizeValue(state.currentUserEmail || state.users?.[0]?.email || 'demo@anforderungsai.de').toLowerCase();

const toQaThreadSummary = (thread) => ({
  threadId: thread.threadId,
  title: thread.title,
  context: thread.context,
  contextSignature: thread.contextSignature,
  createdAt: thread.createdAt,
  updatedAt: thread.updatedAt,
});

const sortThreadsByUpdated = (threads) =>
  [...threads].sort((a, b) => Date.parse(b?.updatedAt ?? '') - Date.parse(a?.updatedAt ?? ''));

const sortMessagesByCreated = (messages) =>
  [...messages].sort((a, b) => Date.parse(a?.createdAt ?? '') - Date.parse(b?.createdAt ?? ''));

const parseMessageLimit = (value, fallback = 200, max = 500) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.min(Math.floor(parsed), max);
};

const matchesQaContext = (conversation, context) => {
  if (!conversation) return false;
  if (context.scope === 'conversation') {
    const scopedId = normalizeValue(context.conversationId);
    if (!scopedId || normalizeValue(conversation.conversationId) !== scopedId) {
      return false;
    }
  }
  const customerId = normalizeValue(context.customerId);
  if (customerId && normalizeValue(conversation.customerId ?? conversation.customer) !== customerId) {
    return false;
  }
  const projectId = normalizeValue(context.projectId);
  if (projectId && normalizeValue(conversation.projectId ?? conversation.project) !== projectId) {
    return false;
  }
  const meetingDate = normalizeValue(conversation.meetingDate);
  const from = normalizeValue(context.from);
  const to = normalizeValue(context.to);
  if (from && meetingDate && meetingDate < from) return false;
  if (to && meetingDate && meetingDate > to) return false;
  return true;
};

const collectQaEvidence = (state, context) => {
  const normalizedContext = normalizeQaContext(context);
  const conversations = (state.conversations ?? []).filter((conversation) => matchesQaContext(conversation, normalizedContext));
  const byConversationId = state.resultsByConversationId ?? {};

  return conversations
    .flatMap((conversation) => {
      const results = byConversationId[conversation.conversationId];
      const requirements = Array.isArray(results?.requirements) ? results.requirements : [];
      return requirements.map((requirement) => ({
        requirement,
        conversation,
      }));
    })
    .filter((entry) => normalizeValue(entry.requirement?.text || entry.requirement?.requirement));
};

const tokenizeQuestion = (question) =>
  normalizeValue(question)
    .toLowerCase()
    .split(/[^a-z0-9äöüß]+/i)
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);

const selectBestEvidence = (evidence, question) => {
  if (!evidence.length) return null;
  const tokens = tokenizeQuestion(question);
  if (!tokens.length) return evidence[0];

  let best = evidence[0];
  let bestScore = -1;
  evidence.forEach((entry) => {
    const text = normalizeValue(entry.requirement?.text || entry.requirement?.requirement).toLowerCase();
    const score = tokens.reduce((sum, token) => (text.includes(token) ? sum + 1 : sum), 0);
    if (score > bestScore) {
      best = entry;
      bestScore = score;
    }
  });
  return best;
};

const buildQaAssistantAnswer = (state, context, question) => {
  const evidence = collectQaEvidence(state, context);
  if (!evidence.length) {
    return {
      answerable: false,
      confidence: 0.2,
      answer: 'Ich kann das im aktuellen Kontext nicht sicher beantworten.',
      explanation:
        'In den vorhandenen Ereignissen oder Transkripten finde ich dazu keinen belastbaren Beleg.',
      followUpQuestion: 'Soll ich dafür global über alle Kunden und Projekte suchen?',
      scopeSuggestion: 'global',
      citations: [],
    };
  }

  const best = selectBestEvidence(evidence, question);
  const requirement = best?.requirement ?? {};
  const conversation = best?.conversation ?? {};
  const answerText = normalizeValue(requirement.text || requirement.requirement);
  const sourceRange = requirement.sourceRange ?? {};
  const startSec = Number(sourceRange.startSec);
  const endSec = Number(sourceRange.endSec);
  const citation = {
    sourceType: 'requirement',
    quote: answerText,
    speaker: normalizeValue(sourceRange.speaker) || null,
    startSec: Number.isFinite(startSec) ? startSec : null,
    endSec: Number.isFinite(endSec) ? endSec : null,
    conversationId: normalizeValue(requirement.conversationId || conversation.conversationId) || null,
    customerId: normalizeValue(requirement.customerId || conversation.customerId) || null,
    projectId: normalizeValue(requirement.projectId || conversation.projectId) || null,
    meetingDate: normalizeValue(requirement.meetingDate || conversation.meetingDate) || null,
    requirementSortKey: normalizeValue(requirement.sortKey) || null,
    confidence: 0.74,
  };

  return {
    answerable: true,
    confidence: 0.74,
    answer: answerText,
    explanation: 'Diese Antwort basiert auf dem passendsten internen Beleg im aktuellen Kontext.',
    followUpQuestion: null,
    scopeSuggestion: null,
    citations: [citation],
  };
};

const buildQaGeneralAnswer = (question) => ({
  answer: `Allgemeinwissen (Test): ${question} lässt sich typischerweise durch Anforderungen an Farbe, Material und Umgebung beantworten.`,
  explanation:
    'Diese Antwort basiert auf allgemeinem Wissen und nicht ausschließlich auf internen Belegen.',
  confidence: 0.45,
});

const createQaMessage = ({ role, content, answerable, confidence, citations, metadata }) => ({
  messageId: createId('qa-msg-'),
  role,
  content,
  answerable: answerable ?? undefined,
  confidence: confidence ?? undefined,
  citations: Array.isArray(citations) ? citations : [],
  metadata: metadata ?? null,
  createdAt: nowIso(),
});

export const createQaThread = async ({ context, title } = {}) => {
  const state = ensureDemoState();
  const ownerEmail = resolveQaOwner(state);
  const threads = ensureQaThreads(state);
  const normalizedContext = normalizeQaContext(context);
  const now = nowIso();
  const thread = {
    threadId: createId('qa-thread-'),
    ownerEmail,
    title: normalizeValue(title) || 'Neuer Q&A-Thread',
    context: normalizedContext,
    contextSignature: qaContextSignature(normalizedContext),
    createdAt: now,
    updatedAt: now,
    messages: [],
  };
  threads.push(thread);
  saveDemoState(state);
  return { thread: toQaThreadSummary(thread) };
};

export const listQaThreads = async ({ limit, contextSignature } = {}) => {
  const state = ensureDemoState();
  const ownerEmail = resolveQaOwner(state);
  const normalizedSignature = normalizeValue(contextSignature);
  const threads = ensureQaThreads(state)
    .filter((thread) => normalizeValue(thread.ownerEmail).toLowerCase() === ownerEmail)
    .filter((thread) => {
      if (!normalizedSignature) return true;
      return qaContextSignature(thread.context) === normalizedSignature;
    })
    .map((thread) => toQaThreadSummary(thread));
  const sorted = sortThreadsByUpdated(threads);
  const safeLimit = parseMessageLimit(limit, 20, 100);
  return sorted.slice(0, safeLimit);
};

export const getQaThread = async (threadId, { messageLimit } = {}) => {
  const state = ensureDemoState();
  const ownerEmail = resolveQaOwner(state);
  const threads = ensureQaThreads(state);
  const thread = threads.find((entry) => entry.threadId === threadId);
  if (!thread || normalizeValue(thread.ownerEmail).toLowerCase() !== ownerEmail) {
    const error = new Error('Thread nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }

  const safeLimit = parseMessageLimit(messageLimit, 200, 500);
  const messages = sortMessagesByCreated(thread.messages ?? []);
  return {
    thread: toQaThreadSummary(thread),
    messages: messages.slice(-safeLimit),
  };
};

export const sendQaMessage = async (threadId, { message, includeGeneralAnswer, searchScope } = {}) => {
  const state = ensureDemoState();
  const ownerEmail = resolveQaOwner(state);
  const threads = ensureQaThreads(state);
  const thread = threads.find((entry) => entry.threadId === threadId);
  if (!thread || normalizeValue(thread.ownerEmail).toLowerCase() !== ownerEmail) {
    const error = new Error('Thread nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }

  const question = normalizeValue(message);
  const normalizedSearchScope = normalizeValue(searchScope).toLowerCase() === 'global' ? 'global' : 'context';
  const effectiveContext = normalizedSearchScope === 'global' ? { scope: 'global' } : thread.context;
  if (!question) {
    const error = new Error('message fehlt.');
    error.code = 'VALIDATION';
    throw error;
  }

  const userMessage = createQaMessage({
    role: 'user',
    content: question,
  });

  const answer = buildQaAssistantAnswer(state, effectiveContext, question);
  const assistantContent =
    answer.answerable || !answer.followUpQuestion
      ? [answer.answer, answer.explanation].filter(Boolean).join('\n\n')
      : `${answer.answer}\n\n${answer.followUpQuestion}`;

  const assistantMessage = createQaMessage({
    role: 'assistant',
    content: assistantContent,
    answerable: answer.answerable,
    confidence: answer.confidence,
    citations: answer.citations,
    metadata: {
      answerVariant: 'internal',
      scopeUsed: normalizedSearchScope,
      ...(answer.scopeSuggestion ? { scopeSuggestion: answer.scopeSuggestion } : {}),
      followUpQuestion: answer.followUpQuestion,
      evidenceCount: answer.citations.length,
    },
  });

  const nextMessages = [userMessage, assistantMessage];

  let assistantGeneralMessage = null;
  if (includeGeneralAnswer === true) {
    const general = buildQaGeneralAnswer(question);
    assistantGeneralMessage = createQaMessage({
      role: 'assistant',
      content: [general.answer, general.explanation].filter(Boolean).join('\n\n'),
      answerable: true,
      confidence: general.confidence,
      citations: [],
      metadata: {
        answerVariant: 'general',
        scopeUsed: normalizedSearchScope,
      },
    });
    nextMessages.push(assistantGeneralMessage);
  }

  thread.messages = sortMessagesByCreated([...(thread.messages ?? []), ...nextMessages]);
  if (!normalizeValue(thread.title) || thread.title === 'Neuer Q&A-Thread') {
    thread.title = question.slice(0, 80);
  }
  thread.updatedAt = (assistantGeneralMessage?.createdAt || assistantMessage.createdAt);
  saveDemoState(state);

  return {
    userMessage,
    assistantMessage,
    ...(assistantGeneralMessage ? { assistantGeneralMessage } : {}),
    answerable: answer.answerable,
    confidence: answer.confidence,
    followUpQuestion: answer.followUpQuestion,
    evidenceCount: answer.citations.length,
    conversationsSearched: collectQaEvidence(state, effectiveContext).length,
  };
};

export const deleteQaThread = async (threadId) => {
  const state = ensureDemoState();
  const ownerEmail = resolveQaOwner(state);
  const threads = ensureQaThreads(state);
  const index = threads.findIndex(
    (thread) => thread.threadId === threadId && normalizeValue(thread.ownerEmail).toLowerCase() === ownerEmail,
  );
  if (index === -1) {
    const error = new Error('Thread nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  threads.splice(index, 1);
  saveDemoState(state);
  return { deleted: true, threadId };
};

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  const escaped = text.replace(/"/g, '""');
  const needsQuotes = /[",\n;]/.test(escaped);
  return needsQuotes ? `"${escaped}"` : escaped;
};

const buildCsv = (rows, delimiter = ',') =>
  rows.map((row) => row.map((cell) => escapeCsvCell(cell)).join(delimiter)).join('\n');

const buildConversationExportRows = (data) => {
  const rows = [];
  rows.push(['kind', 'id', 'type', 'text', 'priority', 'status', 'customer', 'project', 'meetingDate', 'speaker', 'source']);
  (data.requirements ?? []).forEach((req) => {
    const sourceRange = req.sourceRange ?? {};
    rows.push([
      'requirement',
      resolveItemId(req),
      req.type ?? '',
      req.text ?? req.requirement ?? '',
      req.priority ?? '',
      String(req.status ?? 'NEW').toUpperCase(),
      req.customerId ?? '',
      req.projectId ?? '',
      req.meetingDate ?? '',
      sourceRange.speaker ?? '',
      (sourceRange.startSec != null && sourceRange.endSec != null) ? `${sourceRange.startSec}-${sourceRange.endSec}` : '',
    ]);
  });
  (data.userStories ?? []).forEach((story) => {
    rows.push([
      'userStory',
      resolveItemId(story),
      (Array.isArray(story.categories) ? story.categories[0] : '') ?? '',
      story.story ?? '',
      story.priority ?? '',
      '',
      story.customerId ?? '',
      story.projectId ?? '',
      story.meetingDate ?? '',
      story.sourceRange?.speaker ?? '',
      (story.sourceRange?.startSec != null && story.sourceRange?.endSec != null) ? `${story.sourceRange.startSec}-${story.sourceRange.endSec}` : '',
    ]);
  });
  return rows;
};

const buildMarkdown = (data) => {
  const lines = [];
  const conversation = data.conversation ?? {};
  const title = [conversation.customerId, conversation.projectId].filter(Boolean).join(' · ') || 'Export';
  lines.push(`# ${title}`);
  if (conversation.meetingDate) lines.push(`- Meeting: ${conversation.meetingDate}`);
  lines.push('');
  lines.push('## Anforderungen');
  lines.push('');
  (data.requirements ?? []).forEach((req, index) => {
    const id = resolveItemId(req) || `R-${index + 1}`;
    lines.push(`### ${id} · ${req.type ?? 'Anforderung'}`);
    lines.push(req.text ?? req.requirement ?? '');
    const meta = [];
    if (req.priority) meta.push(`Priorität: ${req.priority}`);
    if (req.status) meta.push(`Status: ${String(req.status).toUpperCase()}`);
    if (meta.length) lines.push(`_${meta.join(' · ')}_`);
    lines.push('');
  });
  if ((data.userStories ?? []).length) {
    lines.push('## User Stories');
    lines.push('');
    (data.userStories ?? []).forEach((story, index) => {
      const id = resolveItemId(story) || `US-${index + 1}`;
      lines.push(`### ${id}`);
      lines.push(story.story ?? '');
      lines.push('');
    });
  }
  return lines.join('\n');
};

const toWorksheetRows = (requirements) => {
  const headers = ['ID', 'Typ', 'Text', 'Priorität', 'Status', 'Kunde', 'Projekt', 'Meeting'];
  const rows = [headers];
  (requirements ?? []).forEach((req) => {
    rows.push([
      resolveItemId(req),
      req.type ?? '',
      req.text ?? req.requirement ?? '',
      req.priority ?? '',
      String(req.status ?? 'NEW').toUpperCase(),
      req.customerId ?? '',
      req.projectId ?? '',
      req.meetingDate ?? '',
    ]);
  });
  return rows;
};

export const getExportUrl = async (conversationId, typeOrTemplateId = 'csv') => {
  const state = ensureDemoState();
  const data = await getResults(conversationId);
  const key = normalizeValue(typeOrTemplateId);
  if (!key) throw new Error('Export-Typ fehlt.');

  if (key === 'csv') {
    const csv = buildCsv(buildConversationExportRows(data), ',');
    const blob = new Blob([csv], { type: 'text/csv' });
    return createBlobUrl(blob);
  }

  if (key === 'md') {
    const md = buildMarkdown(data);
    const blob = new Blob([md], { type: 'text/markdown' });
    return createBlobUrl(blob);
  }

  const template = findTemplateById(state, key);
  if (!template) {
    throw new Error('Vorlage nicht gefunden.');
  }

  if (template.format === 'xlsx') {
    const blob = buildXlsxBlob({ sheetName: 'Export', rows: toWorksheetRows(data.requirements) });
    return createBlobUrl(blob);
  }
  if (template.format === 'docx') {
    const title = [data.conversation?.customerId, data.conversation?.projectId].filter(Boolean).join(' · ') || 'Export';
    const paragraphs = (data.requirements ?? []).map((req) => `${resolveItemId(req)} · ${req.type ?? 'Anforderung'}: ${req.text ?? req.requirement ?? ''}`);
    const blob = buildDocxBlob({ title, paragraphs });
    return createBlobUrl(blob);
  }

  throw new Error('Unbekanntes Export-Format.');
};

export const exportRequirements = async ({ templateId, requirements } = {}) => {
  const state = ensureDemoState();
  const template = templateId ? findTemplateById(state, templateId) : null;
  if (!template) {
    throw new Error('Vorlage nicht gefunden.');
  }

  if (template.format === 'xlsx') {
    const blob = buildXlsxBlob({ sheetName: 'Anforderungen', rows: toWorksheetRows(requirements ?? []) });
    return { url: createBlobUrl(blob) };
  }
  if (template.format === 'docx') {
    const paragraphs = (requirements ?? []).map((req) => `${resolveItemId(req)} · ${req.type ?? 'Anforderung'}: ${req.text ?? req.requirement ?? ''}`);
    const blob = buildDocxBlob({ title: 'Anforderungen', paragraphs });
    return { url: createBlobUrl(blob) };
  }

  const csv = buildCsv(toWorksheetRows(requirements ?? []), ',');
  const blob = new Blob([csv], { type: 'text/csv' });
  return { url: createBlobUrl(blob) };
};

export const getExportTemplates = async () => {
  const state = ensureDemoState();
  return {
    systemTemplates: state.exportTemplates?.systemTemplates ?? [],
    userTemplates: state.exportTemplates?.userTemplates ?? [],
  };
};

export const getExportTemplate = async (templateId) => {
  const state = ensureDemoState();
  const template = findTemplateById(state, templateId);
  if (!template) {
    const error = new Error('Vorlage nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  return { template };
};

const applyDefaultTemplate = (templates, format, selectedTemplateId) =>
  templates.map((t) => ({
    ...t,
    isDefault: t.format === format ? t.templateId === selectedTemplateId : Boolean(t.isDefault),
  }));

export const createExportTemplate = async (template) => {
  const state = ensureDemoState();
  const next = {
    ...template,
    templateId: createId('usr-tpl-'),
  };
  state.exportTemplates.userTemplates = Array.isArray(state.exportTemplates?.userTemplates)
    ? [...state.exportTemplates.userTemplates, next]
    : [next];
  if (next.isDefault) {
    state.exportTemplates.userTemplates = applyDefaultTemplate(state.exportTemplates.userTemplates, next.format, next.templateId);
  }
  saveDemoState(state);
  return { template: next };
};

export const updateExportTemplate = async (templateId, updates) => {
  const state = ensureDemoState();
  const list = Array.isArray(state.exportTemplates?.userTemplates) ? [...state.exportTemplates.userTemplates] : [];
  const idx = list.findIndex((t) => t.templateId === templateId);
  if (idx === -1) {
    const error = new Error('Vorlage nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  const next = { ...list[idx], ...updates, templateId };
  list[idx] = next;
  let updatedList = list;
  if (next.isDefault) {
    updatedList = applyDefaultTemplate(updatedList, next.format, next.templateId);
  }
  state.exportTemplates.userTemplates = updatedList;
  saveDemoState(state);
  return { template: next };
};

export const deleteExportTemplate = async (templateId) => {
  const state = ensureDemoState();
  state.exportTemplates.userTemplates = (state.exportTemplates?.userTemplates ?? []).filter((t) => t.templateId !== templateId);
  saveDemoState(state);
  return { ok: true };
};

export const presignUpload = async ({ filename, contentType, customerId, projectId, uploadType, engine, meetingDate } = {}) => {
  const state = ensureDemoState();
  const conversationId = createId('demo-conv-');
  const createdAt = nowIso();
  const mode = uploadType === 'transcript' ? 'transcript' : 'upload';

  const conversation = {
    conversationId,
    customerId: normalizeValue(customerId) || 'Demo Kunde',
    projectId: normalizeValue(projectId) || 'Demo Projekt',
    meetingDate: normalizeValue(meetingDate) || createdAt.slice(0, 10),
    status: 'UPLOADED',
    mode,
    originalFilename: normalizeValue(filename) || 'upload',
    transcriptionEngine: engine || (mode === 'transcript' ? 'aws-transcribe' : 'mistral'),
    createdAt,
    updatedAt: createdAt,
    changeLog: [],
  };

  state.conversations = Array.isArray(state.conversations) ? [conversation, ...state.conversations] : [conversation];

  const base = pickBaseResults(state, {
    customerId: conversation.customerId,
    projectId: conversation.projectId,
    meetingDate: conversation.meetingDate,
    mode,
  });
  const requirements = cloneWithConversationMeta(base.requirements, conversation);
  const userStories = cloneWithConversationMeta(base.userStories, conversation);

  state.resultsByConversationId = state.resultsByConversationId ?? {};
  state.resultsByConversationId[conversationId] = {
    conversation,
    requirements,
    userStories,
    upload: {
      filename: normalizeValue(filename),
      contentType: normalizeValue(contentType),
    },
  };

  updateUserJobStats(state, state.currentUserEmail);
  saveDemoState(state);

  const uploadUrl = `demo://upload?conversationId=${encodeURIComponent(conversationId)}`;
  await delay(250);
  return { uploadUrl, conversationId };
};

const parseConversationIdFromUploadUrl = (url) => {
  const raw = normalizeValue(url);
  if (!raw) return '';
  const match = raw.match(/[?&]conversationId=([^&]+)/i);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const uploadFileToPresignedUrl = async (url, file) => {
  const state = ensureDemoState();
  const conversationId = parseConversationIdFromUploadUrl(url);
  const conversation = (state.conversations ?? []).find((c) => c.conversationId === conversationId) ?? null;
  if (conversation) {
    conversation.status = 'EXTRACTING';
    conversation.updatedAt = nowIso();
    saveDemoState(state);
  }
  await delay(400);
  if (conversation) {
    conversation.status = 'READY';
    conversation.updatedAt = nowIso();
    const payload = state.resultsByConversationId?.[conversationId];
    if (payload) {
      payload.conversation = conversation;
      payload.upload = payload.upload ?? {};
      payload.upload.size = file?.size ?? null;
    }
    saveDemoState(state);
  }
};

export const finalizeManualTranscript = async (conversationId) => {
  const state = ensureDemoState();
  const conversation = (state.conversations ?? []).find((c) => c.conversationId === conversationId) ?? null;
  if (conversation) {
    conversation.status = 'READY';
    conversation.updatedAt = nowIso();
    const payload = state.resultsByConversationId?.[conversationId];
    if (payload) payload.conversation = conversation;
    saveDemoState(state);
  }
  await delay(200);
  return { ok: true };
};

export const createCheckoutSession = async () => {
  throw new Error('Stripe Checkout ist im Demo-Modus deaktiviert.');
};

export const createSubscription = async () => {
  throw new Error('Billing ist im Demo-Modus deaktiviert.');
};

export const getBillingInvoices = async () => {
  const state = ensureDemoState();
  return { invoices: state.billing?.invoices ?? [] };
};

export const getBillingPaymentMethod = async () => {
  const state = ensureDemoState();
  const paymentMethods = state.billing?.paymentMethods ?? [];
  const defaultPaymentMethod = paymentMethods.find((pm) => pm.isDefault) ?? paymentMethods[0] ?? null;
  return { paymentMethods, defaultPaymentMethod };
};

export const createPaymentMethodSetup = async () => {
  throw new Error('Neue Zahlungsmethoden sind im Demo-Modus deaktiviert.');
};

export const updatePaymentMethod = async ({ paymentMethodId } = {}) => {
  const state = ensureDemoState();
  const list = Array.isArray(state.billing?.paymentMethods) ? [...state.billing.paymentMethods] : [];
  const idx = list.findIndex((pm) => pm.id === paymentMethodId);
  if (idx === -1) {
    throw new Error('Zahlungsmethode nicht gefunden.');
  }
  const updated = list.map((pm, index) => ({ ...pm, isDefault: index === idx }));
  state.billing.paymentMethods = updated;
  const defaultPm = updated[idx];
  const user = state.users.find((u) => toKey(u.email) === toKey(state.currentUserEmail));
  if (user) {
    user.billing = {
      method: defaultPm.method ?? 'card',
      brand: defaultPm.brand,
      last4: defaultPm.last4,
      expMonth: defaultPm.expMonth,
      expYear: defaultPm.expYear,
    };
    user.billingStatus = 'active';
    user.updatedAt = nowIso();
  }
  saveDemoState(state);
  return { ok: true };
};

export const getBillingHistory = async () => {
  const state = ensureDemoState();
  return { history: state.billing?.history ?? [] };
};

export const createLiveSession = async (payload = {}) => {
  const state = ensureDemoState();
  const sessionId = createId('demo-live-');
  const session = {
    sessionId,
    status: 'LIVE_ACTIVE',
    createdAt: nowIso(),
    customerId: payload.customerId ?? null,
    projectId: payload.projectId ?? null,
    meetingDate: payload.meetingDate ?? null,
    language: payload.language ?? null,
    transcriptionEngine: payload.engine ?? payload.transcriptionEngine ?? 'mistral',
    transcript: null,
    requirements: { items: [] },
    userStories: { items: [] },
    extractionWarnings: [],
    sequence: 0,
  };
  state.live.sessionsById = state.live?.sessionsById ?? {};
  state.live.sessionsById[sessionId] = session;
  saveDemoState(state);
  await delay(150);
  return session;
};

export const sendLiveChunk = async (sessionId, payload = {}) => {
  const state = ensureDemoState();
  const session = state.live?.sessionsById?.[sessionId];
  if (!session) {
    const error = new Error('Live-Session nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  const nextSeq = Number(payload.sequence ?? session.sequence ?? 0);
  session.sequence = nextSeq;
  const text = nextSeq === 0
    ? 'Wir starten mit den wichtigsten Punkten…'
    : nextSeq === 1
      ? 'Priorität hat die schnelle Reaktion auf Peak-Last.'
      : 'Export und Status-Review sind für das Team wichtig.';

  const base = state.conversations?.[0]?.conversationId
    ? state.resultsByConversationId?.[state.conversations[0].conversationId]
    : null;
  const seedReqs = Array.isArray(base?.requirements) ? base.requirements : [];
  const items = seedReqs.slice(0, Math.min(3, seedReqs.length)).map((req) => ({
    type: req.type ?? 'Anforderung',
    text: req.text ?? req.requirement ?? '',
  }));

  session.requirements = { items };
  session.language = payload.language ?? session.language;
  session.transcript = { sequence: nextSeq, text };
  saveDemoState(state);
  await delay(120);
  return {
    transcript: session.transcript,
    requirements: session.requirements,
    language: session.language,
    transcriptionEngine: session.transcriptionEngine,
  };
};

export const stopLiveSession = async (sessionId) => {
  const state = ensureDemoState();
  const session = state.live?.sessionsById?.[sessionId];
  if (!session) {
    const error = new Error('Live-Session nicht gefunden.');
    error.code = 'NOT_FOUND';
    throw error;
  }
  session.status = 'COMPLETED';
  saveDemoState(state);
  await delay(120);
  return session;
};

export const listAppFactorySessions = async () => {
  const state = ensureDemoState();
  return { sessions: state.appFactory?.sessions ?? [] };
};

export const createAppFactorySession = async (data = {}) => {
  const state = ensureDemoState();
  const sessionId = createId('demo-af-');
  const createdAt = nowIso();
  const session = {
    sessionId,
    status: 'in_progress',
    guidelineId: data.guidelineId ?? 'default-v1',
    displayName: data.projectId ? `Session · ${data.projectId}` : `Session ${sessionId.slice(0, 8)}`,
    customerId: data.customerId ?? null,
    projectId: data.projectId ?? null,
    ownerEmail: state.currentUserEmail ?? 'demo@anforderungsai.de',
    createdAt,
    progress: {
      percentComplete: 0,
      completedCategories: 0,
      totalCategories: 3,
      categories: [
        { name: 'Ziele & Nutzer', completed: false, confidence: 0.3, questions: [{ text: 'Wer nutzt die Lösung?', answered: false }] },
        { name: 'Funktionen', completed: false, confidence: 0.3, questions: [{ text: 'Welche Kernfunktionen sind nötig?', answered: false }] },
        { name: 'Nicht-funktionale Anforderungen', completed: false, confidence: 0.3, questions: [{ text: 'Welche SLAs sind wichtig?', answered: false }] },
      ],
    },
    extractedRequirements: [],
  };
  state.appFactory.sessions = Array.isArray(state.appFactory?.sessions) ? [session, ...state.appFactory.sessions] : [session];
  saveDemoState(state);
  await delay(150);
  return { sessionId };
};

export const getAppFactorySession = async (sessionId) => {
  const state = ensureDemoState();
  const session = (state.appFactory?.sessions ?? []).find((s) => s.sessionId === sessionId) ?? null;
  if (!session) {
    const error = new Error('Session not found');
    error.code = 'NOT_FOUND';
    throw error;
  }
  return { session, extractedRequirements: session.extractedRequirements ?? [] };
};

export const analyzeAppFactoryProgress = async (sessionId) => {
  const state = ensureDemoState();
  const session = (state.appFactory?.sessions ?? []).find((s) => s.sessionId === sessionId) ?? null;
  if (!session) {
    const error = new Error('Session not found');
    error.code = 'NOT_FOUND';
    throw error;
  }
  const progress = session.progress ?? {};
  const completedCategories = Math.min(progress.totalCategories ?? 3, Number(progress.completedCategories ?? 0) + 1);
  const totalCategories = progress.totalCategories ?? 3;
  const percentComplete = Math.round((completedCategories / totalCategories) * 100);
  progress.completedCategories = completedCategories;
  progress.percentComplete = percentComplete;
  progress.categories = (progress.categories ?? []).map((cat, idx) => ({
    ...cat,
    completed: idx < completedCategories,
    confidence: cat.confidence ?? 0.6,
  }));
  session.progress = progress;
  saveDemoState(state);
  await delay(250);
  return { progress };
};

export const updateAppFactoryRequirements = async (sessionId, requirements = {}) => {
  const state = ensureDemoState();
  const session = (state.appFactory?.sessions ?? []).find((s) => s.sessionId === sessionId) ?? null;
  if (!session) {
    const error = new Error('Session not found');
    error.code = 'NOT_FOUND';
    throw error;
  }
  const functional = Array.isArray(requirements.functional) ? requirements.functional : [];
  const nonFunctional = Array.isArray(requirements.nonFunctional) ? requirements.nonFunctional : [];
  session.extractedRequirements = [...functional, ...nonFunctional].map((req) => ({
    type: req.type ?? 'Funktional',
    text: req.text ?? req.description ?? '',
    priority: req.priority ?? 'medium',
  }));
  saveDemoState(state);
  await delay(150);
  return { session };
};

const toYaml = (obj) => {
  const lines = [];
  const indent = (level) => '  '.repeat(level);
  const walk = (value, level, key) => {
    if (Array.isArray(value)) {
      lines.push(`${indent(level)}${key}:`);
      value.forEach((entry) => {
        if (entry && typeof entry === 'object') {
          lines.push(`${indent(level + 1)}-`);
          Object.entries(entry).forEach(([k, v]) => walk(v, level + 2, k));
        } else {
          lines.push(`${indent(level + 1)}- ${String(entry)}`);
        }
      });
      return;
    }
    if (value && typeof value === 'object') {
      lines.push(`${indent(level)}${key}:`);
      Object.entries(value).forEach(([k, v]) => walk(v, level + 1, k));
      return;
    }
    lines.push(`${indent(level)}${key}: ${String(value ?? '')}`);
  };
  Object.entries(obj ?? {}).forEach(([k, v]) => walk(v, 0, k));
  return lines.join('\n');
};

export const exportAppFactoryYaml = async (sessionId) => {
  const state = ensureDemoState();
  const session = (state.appFactory?.sessions ?? []).find((s) => s.sessionId === sessionId) ?? null;
  if (!session) {
    const error = new Error('Session not found');
    error.code = 'NOT_FOUND';
    throw error;
  }
  const yaml = toYaml({
    sessionId: session.sessionId,
    customerId: session.customerId ?? '',
    projectId: session.projectId ?? '',
    requirements: session.extractedRequirements ?? [],
  });
  await delay(120);
  return { yaml };
};

export const deleteAppFactorySession = async (sessionId) => {
  const state = ensureDemoState();
  state.appFactory.sessions = (state.appFactory?.sessions ?? []).filter((s) => s.sessionId !== sessionId);
  saveDemoState(state);
  await delay(120);
  return { ok: true };
};

export const importAppFactoryRequirements = async (sessionId, conversationId) => {
  const state = ensureDemoState();
  const session = (state.appFactory?.sessions ?? []).find((s) => s.sessionId === sessionId) ?? null;
  if (!session) {
    throw new Error('Session not found');
  }
  const data = state.resultsByConversationId?.[conversationId];
  if (!data) {
    throw new Error('Conversation not found');
  }
  session.extractedRequirements = (data.requirements ?? []).slice(0, 10).map((req) => ({
    type: req.type ?? 'Funktional',
    text: req.text ?? req.requirement ?? '',
    priority: req.priority ?? 'medium',
  }));
  saveDemoState(state);
  await delay(150);
  return { ok: true };
};

export const getReleaseNotes = async () => ({ releases: [], latestReleaseId: null });

export const getUnseenReleaseNotes = async () => ({ releases: [], latestReleaseId: null });

export const ackReleaseNotes = async () => ({ ok: true });

export const getGeoLanguage = async () => ({ language: 'en', country: null });
